import React, { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSelector, shallowEqual } from 'react-redux';
import QuestionDistributionChart from './QuestionDistributionChart';
import QuestionContent from './QuestionContent';
import Anchor from '../Anchor';
import { filter_data } from 'reports/Tag/calculations';
import ScheduleBreadcrumb from '../../ScheduleV2/ScheduleBreadcrumb';

// Local palette (avoid global style deps)
const COLOR_COMPLETE = '#1D9A7D';

function isValidResponse(value) {
    if (value === null || value === undefined) return false;
    if (value === 'N/A' || value === '') return false;
    if (isNaN(Number(value))) return false;
    return true;
}

const BackIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20z" />
    </svg>
);

const QuestionCompliance = ({ core_data, data }) => {
    const [viewMode, setViewMode] = useState('questions'); // 'questions' | 'factors'
    const [selected, setSelected] = useState(null); // question or factor object
    const [sortDirection, setSortDirection] = useState('desc'); // 'desc' | 'asc'

    useSelector((state) => state.audit?.anchor, shallowEqual); // ensure store subscription for anchor updates
    const selectedFilters = useSelector((state) => state.audit?.new_filters, shallowEqual);

    const correctedData = useMemo(() => {
        if (!data) return [];
        let raw = data;
        if (Array.isArray(data) && data[0] && Array.isArray(data[0])) {
            raw = data[0];
        }
        try {
            return filter_data(raw, selectedFilters || []);
        } catch (e) {
            return raw;
        }
    }, [data, selectedFilters]);

    const allQuestions = useMemo(() => {
        if (!core_data) return [];
        const standardQs = (core_data.questions?.dimensions || []).reduce((acc, dimension, dimensionIndex) => {
            const items = (dimension.factors || []).reduce((fAcc, factor, factorIndex) => {
                const qs = (factor.questions || []).map((q, questionIndex) => ({
                    key: `${dimensionIndex}-${factorIndex}-${questionIndex}`,
                    dimension: dimensionIndex,
                    factor: factorIndex,
                    q: questionIndex,
                    title: q.q || `Question ${questionIndex + 1}`,
                    type: 'question',
                }));
                return fAcc.concat(qs);
            }, []);
            return acc.concat(items);
        }, []);

        const outcomeQs = (core_data.outcomeQuestions?.questions || []).reduce((acc, category, categoryIndex) => {
            const items = (category.questions || []).map((oq, oqIndex) => ({
                key: `outcome-q-${categoryIndex}-${oqIndex}`,
                outcomeCategory: categoryIndex,
                q: oq.q,
                s: oq.s,
                title: oq.question || `${category.name} Q${oqIndex + 1}`,
                type: 'outcome_question',
                isOutcome: true,
            }));
            return acc.concat(items);
        }, []);

        return [...standardQs, ...outcomeQs];
    }, [core_data]);

    const allFactors = useMemo(() => {
        if (!core_data) return [];
        const standard = (core_data.questions?.dimensions || []).reduce((acc, dimension, dimensionIndex) => {
            const items = (dimension.factors || []).map((factor, factorIndex) => ({
                key: `factor-${dimensionIndex}-${factorIndex}`,
                dimension: dimensionIndex,
                factor: factorIndex,
                title: factor.title || `Factor ${factorIndex + 1}`,
                questionCount: (factor.questions || []).length,
                type: 'factor',
            }));
            return acc.concat(items);
        }, []);

        const outcomeCats = (core_data.outcomeQuestions?.questions || []).map((category, categoryIndex) => ({
            key: `outcome-factor-${categoryIndex}`,
            outcomeCategory: categoryIndex,
            title: category.name,
            questionCount: (category.questions || []).length,
            type: 'outcome_factor',
            isOutcome: true,
        }));

        return [...standard, ...outcomeCats];
    }, [core_data]);

    function computeQuestionCompliance(question) {
        if (!correctedData || correctedData.length === 0) return { completed: 0, total: 0, percent: 0 };
        let completedCount = 0;
        let totalCount = 0;

        if (question.isOutcome || question.type === 'outcome_question') {
            for (const response of correctedData) {
                totalCount += 1; // one potential response per person
                const match = (response.employee_outcomes?.responses || []).find((r) => String(r.q) === String(question.q) && String(r.s) === String(question.s));
                if (match && isValidResponse(match.response)) completedCount += 1;
            }
        } else {
            for (const response of correctedData) {
                totalCount += 1; // one potential response per person
                const match = (response.questions || []).find((q) =>
                    q.id === question.dimension && q.factor === question.factor && q.q === question.q
                );
                if (match && isValidResponse(match.response)) completedCount += 1;
            }
        }

        const percent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
        return { completed: completedCount, total: totalCount, percent };
    }

    function computeFactorCompliance(factorInfo) {
        if (!correctedData || correctedData.length === 0) return { completed: 0, total: 0, percent: 0 };
        let completedCount = 0;
        let totalCount = 0;

        if (factorInfo.isOutcome || factorInfo.type === 'outcome_factor') {
            const category = core_data?.outcomeQuestions?.questions?.[factorInfo.outcomeCategory];
            const questionCount = category?.questions?.length || 0;
            for (const response of correctedData) {
                totalCount += questionCount; // potential per person = questions in category
                const matches = (response.employee_outcomes?.responses || []).filter((r) =>
                    (category?.questions || []).some((oq) => String(oq.q) === String(r.q) && String(oq.s) === String(r.s))
                );
                completedCount += matches.filter((m) => isValidResponse(m.response)).length;
            }
        } else {
            const factor = core_data?.questions?.dimensions?.[factorInfo.dimension]?.factors?.[factorInfo.factor];
            const questionCount = factor?.questions?.length || 0;
            for (const response of correctedData) {
                totalCount += questionCount; // potential per person = questions in factor
                const matches = (response.questions || []).filter((q) =>
                    q.id === factorInfo.dimension && q.factor === factorInfo.factor
                );
                completedCount += matches.filter((m) => isValidResponse(m.response)).length;
            }
        }

        const percent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
        return { completed: completedCount, total: totalCount, percent };
    }

    const questionRows = useMemo(() => {
        return allQuestions.map((q) => {
            const stats = computeQuestionCompliance(q);
            return { ...q, ...stats };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allQuestions, data]);

    const factorRows = useMemo(() => {
        return allFactors.map((f) => {
            const stats = computeFactorCompliance(f);
            return { ...f, ...stats };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allFactors, data]);

    const sortedRows = useMemo(() => {
        const rows = viewMode === 'questions' ? questionRows : factorRows;
        const copy = [...rows];
        copy.sort((a, b) => (sortDirection === 'desc' ? b.percent - a.percent : a.percent - b.percent));
        return copy;
    }, [viewMode, questionRows, factorRows, sortDirection]);

    const breadcrumbCrumbs = useMemo(() => {
        if (selected) {
            return [
                {
                    label: 'Question Compliance',
                    onClick: () => setSelected(null),
                },
                { label: selected.title || 'Details' },
            ];
        }
        return [{ label: 'Question Compliance' }];
    }, [selected, setSelected]);

    if (!core_data) {
        return <div>Loading…</div>;
    }

    return (
        <Container>
            <ScheduleBreadcrumb crumbs={breadcrumbCrumbs} />
            {/* <SurveyBackRow>
                <SurveyBackButton type="button" onClick={() => navigate(-1)}>
                    <BackIcon />
                    <span>Back to survey</span>
                </SurveyBackButton>
            </SurveyBackRow> */}
            {!selected ? (
                <FadeWrap key={`list-${viewMode}-${sortDirection}`}>
                    <HeaderRow>
                        <Title>Participation by question</Title>
                        <Controls>
                            <Segmented>
                                <SegButton
                                    isActive={viewMode === 'questions'}
                                    onClick={() => setViewMode('questions')}
                                    aria-pressed={viewMode === 'questions'}
                                >
                                    Questions
                                </SegButton>
                                <SegButton
                                    isActive={viewMode === 'factors'}
                                    onClick={() => setViewMode('factors')}
                                    aria-pressed={viewMode === 'factors'}
                                >
                                    Factors
                                </SegButton>
                            </Segmented>
                            <SortWrap>
                                <SortButton onClick={() => setSortDirection((p) => (p === 'desc' ? 'asc' : 'desc'))}>
                                    {sortDirection === 'desc' ? 'High → Low' : 'Low → High'}
                                </SortButton>
                            </SortWrap>
                        </Controls>
                    </HeaderRow>

                    <List>
                        {sortedRows.map((row) => {
                            const dataForChart = [
                                { name: 'Complete', data: [row.percent], color: COLOR_COMPLETE, count: row.completed },
                            ];
                            return (
                                <ListItem key={row.key} onClick={() => setSelected(row)}>
                                    <ItemTitle>{row.title}</ItemTitle>
                                    <ItemChart>
                                        <QuestionDistributionChart data={dataForChart} />
                                        <Pct>{Math.round(row.percent)}%</Pct>
                                    </ItemChart>
                                </ListItem>
                            );
                        })}
                        {sortedRows.length === 0 && (
                            <EmptyState>No data available.</EmptyState>
                        )}
                    </List>
                </FadeWrap>
            ) : (
                <Detail as={SlideIn}>
                    <DetailHeader>
                        <DetailBackButton onClick={() => setSelected(null)}>Back</DetailBackButton>
                        <Anchor anchorLabel=" " />
                    </DetailHeader>

                    <QuestionContent
                        selected={selected}
                        core_data={core_data}
                        data={correctedData}
                    />
                </Detail>
            )}
        </Container>
    );
};

export default QuestionCompliance;

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
`;

// Motion primitives
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FadeWrap = styled.div`
  animation: ${fadeIn} 180ms ease-out;
`;

const SlideIn = styled.div`
  animation: ${slideIn} 200ms ease-out;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #111827;
`;

const Controls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const Segmented = styled.div`
    display: inline-flex;
    background: #f3f4f6;
    border-radius: 8px;
    padding: 2px;
`;

const SegButton = styled.button`
    appearance: none;
    border: 0;
    background: ${(p) => (p.isActive ? '#6B7280' : 'transparent')};
    color: ${(p) => (p.isActive ? '#ffffff' : '#111827')};
    padding: 6px 10px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    transition: background 160ms ease;
    &:hover { background: ${(p) => (p.isActive ? '#6B7280' : '#e5e7eb')}; }
`;

const SortWrap = styled.div`
`;

const SortButton = styled.button`
    appearance: none;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #111827;
    border-radius: 8px;
    padding: 6px 10px;
    cursor: pointer;
    font-weight: 600;
    &:hover { border-color: #d1d5db; }
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const ListItem = styled.div`
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 18px;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid #f3f4f6;
    cursor: pointer;
    &:hover { background: #fafafa; }
`;

const ItemTitle = styled.div`
    color: #111827;
    font-weight: 600;
`;

const ItemChart = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 10px;
`;

const Pct = styled.div`
    font-weight: 700;
    color: #111827;
`;

const EmptyState = styled.div`
    padding: 24px;
    color: #6b7280;
`;

const Detail = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const DetailHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const SurveyBackRow = styled.div`
    display: flex;
    justify-content: flex-start;
`;

const SurveyBackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: transparent;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    color: #7e7e7e;
    font-family: "Poppins";
    font-weight: 400;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #f8f9fa;
        border-color: #c0c0c0;
    }

    svg {
        display: block;
    }
`;

const DetailBackButton = styled.button`
    appearance: none;
    border: 0;
    background: transparent;
    color: #2563eb;
    font-weight: 600;
    cursor: pointer;
`;

// removed AnchorNote; Anchor dropdown is rendered instead
